// API-Client für den Tab „formularfrei" (/api/analytics/formularfrei)
//
// Eigene Datei statt eines Anbaus an analytics-api.ts: Das hier ist kein
// Analytics-Endpunkt, sondern die Verwaltung eines Moduls, das in einem
// ANDEREN Produkt läuft. Getrennt zu halten kostet nichts und macht später
// sichtbar, was zu formularfrei.ai gehört und was nicht.

import { supabase } from './supabase';

export interface FfLeistung {
  id: string;
  label: string;
  beschreibung?: string | null;
  /** Der Schalter im Tab: Wird das im Modul angeboten? */
  angeboten: boolean;
}

export interface FfZahlen {
  /** Monat der Zahlen (YYYY-MM). */
  monat: string;
  bilder_monat: number;
  anfragen_monat: number;
  /** Besucher, die ein Bild gesehen und trotzdem nicht angefragt haben. */
  ohne_anfrage: number;
}

export interface FormularfreiStand {
  /** Ist die Verbindung zu formularfrei.ai serverseitig eingerichtet? */
  konfiguriert: boolean;
  /** Hat formularfrei.ai geantwortet? */
  erreichbar: boolean;
  fehler?: string | null;
  projekt: { id: string; name: string; slug: string; website: string | null };
  kennung: string;
  adresse: string | null;
  /** https://<adresse>.formularfrei.ai */
  seite: string | null;
  eingerichtet: boolean;
  aktiv: boolean;
  /**
   * Kommen die Anfragen auch im Posteingang an? Das ist etwas anderes als
   * `eingerichtet`: Das Modul kann drüben laufen und trotzdem ins Leere
   * liefern, solange die Verbindung ins LMS fehlt.
   */
  zustellung: boolean;
  /** Die zwei Zeilen zum Einbauen. */
  einbau: string;
  leistungen: FfLeistung[];
  zahlen: FfZahlen | null;
}

async function mitLogin(pfad: string, init?: RequestInit): Promise<Response> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Nicht eingeloggt');
  return fetch(pfad, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
      ...(init?.headers || {}),
    },
  });
}

async function alsJson<T>(res: Response): Promise<T> {
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((json as { error?: string }).error || `Fehler (HTTP ${res.status})`);
  return json as T;
}

export async function getFormularfrei(projectId: string): Promise<FormularfreiStand> {
  const res = await mitLogin(`/api/analytics/formularfrei?project_id=${encodeURIComponent(projectId)}`);
  return alsJson<FormularfreiStand>(res);
}

/** Website des Betriebs lesen und das Modul anlegen. Dauert bis zu zwei Minuten. */
export async function richteFormularfreiEin(projectId: string): Promise<FormularfreiStand> {
  const res = await mitLogin('/api/analytics/formularfrei', {
    method: 'POST',
    body: JSON.stringify({ project_id: projectId, aktion: 'einrichten' }),
  });
  return alsJson<FormularfreiStand>(res);
}

/** Die Zustellung in den Posteingang (neu) verbinden. Der Reparaturweg. */
export async function verbindeZustellung(projectId: string): Promise<FormularfreiStand> {
  const res = await mitLogin('/api/analytics/formularfrei', {
    method: 'POST',
    body: JSON.stringify({ project_id: projectId, aktion: 'zustellung' }),
  });
  return alsJson<FormularfreiStand>(res);
}

/** Eine Leistung an- oder abschalten. Antwortet mit dem neuen Gesamtstand. */
export async function schalteLeistung(
  projectId: string,
  leistungId: string,
  angeboten: boolean
): Promise<FormularfreiStand> {
  const res = await mitLogin('/api/analytics/formularfrei', {
    method: 'POST',
    body: JSON.stringify({ project_id: projectId, aktion: 'leistung', leistung_id: leistungId, angeboten }),
  });
  return alsJson<FormularfreiStand>(res);
}
